/**
 * TradingStatusBar
 * ================
 * Global status bar — always visible at the top of every page — that
 * answers "why aren't we trading?" in real time.
 *
 * Polls GET /api/titan/why-no-trades every 30 s.
 * Collapses to a single line; click to expand the full issue list.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  Activity,
  Bot,
  RefreshCw,
} from 'lucide-react';
import { titanApi, TradingHealth } from '../lib/api';

// ── helpers ──────────────────────────────────────────────────────────────────

const STATUS_META: Record<
  string,
  { bg: string; border: string; text: string; dot: string; Icon: React.ElementType }
> = {
  ready:    { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-700',  dot: 'bg-emerald-500',  Icon: CheckCircle2 },
  degraded: { bg: 'bg-amber-500/10',   border: 'border-amber-500/30',   text: 'text-amber-700',    dot: 'bg-amber-500',    Icon: AlertTriangle },
  blocked:  { bg: 'bg-red-500/10',     border: 'border-red-500/30',     text: 'text-red-700',      dot: 'bg-red-500',      Icon: XCircle },
  closed:   { bg: 'bg-muted/40',       border: 'border-muted',          text: 'text-muted-foreground', dot: 'bg-muted-foreground', Icon: Clock },
  error:    { bg: 'bg-red-500/10',     border: 'border-red-500/30',     text: 'text-red-700',      dot: 'bg-red-500',      Icon: XCircle },
};

function tierBadge(count: number, label: string, color: string) {
  if (count === 0) return null;
  return (
    <span key={label} className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold ${color}`}>
      {count} {label}
    </span>
  );
}

// ── component ─────────────────────────────────────────────────────────────────

const TradingStatusBar: React.FC = () => {
  const [health, setHealth] = useState<TradingHealth | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lastFetch, setLastFetch] = useState<Date | null>(null);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    try {
      const data = await titanApi.whyNoTrades();
      setHealth(data);
      setLastFetch(new Date());
    } catch {
      // leave stale data on screen; next poll will retry
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    const id = setInterval(fetchHealth, 30_000);
    return () => clearInterval(id);
  }, [fetchHealth]);

  if (!health) return null;          // don't render until first fetch

  const meta = STATUS_META[health.status] ?? STATUS_META.degraded;
  const { Icon } = meta;
  const tiers = health.deploy_tiers;
  const scanLoop = health.scan_loop;
  const scanStale = scanLoop?.stale === true;

  return (
    <div className={`rounded-lg border ${meta.bg} ${meta.border} px-4 py-2 mb-3 transition-colors`}>
      {/* ── collapsed / summary row ───────────────────────────────────── */}
      <div className="flex items-center gap-2 cursor-pointer select-none" onClick={() => setExpanded(e => !e)}>
        {/* status dot */}
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${meta.dot} ${health.status === 'ready' ? 'animate-pulse' : ''}`} />

        {/* icon + top reason */}
        <Icon className={`w-4 h-4 flex-shrink-0 ${meta.text}`} />
        <span className={`text-sm font-medium flex-1 min-w-0 truncate ${meta.text}`}>
          {health.top_reason}
        </span>

        {/* tier pills */}
        {tiers && (
          <div className="hidden sm:flex items-center gap-1 flex-shrink-0">
            {tierBadge(tiers.live,       'live',       'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300')}
            {tierBadge(tiers.scout,      'scout',      'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300')}
            {tierBadge(tiers.shadow,     'shadow',     'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300')}
            {tierBadge(tiers.quarantine, 'quarantine', 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300')}
          </div>
        )}

        {/* scan loop indicator */}
        {scanLoop && (
          <span
            title={
              scanLoop.running
                ? scanStale
                  ? `Scan loop stale (last tick ${scanLoop.last_tick_seconds_ago}s ago)`
                  : `Scan loop OK (last tick ${scanLoop.last_tick_seconds_ago}s ago)`
                : 'Scan loop stopped'
            }
            className={`hidden sm:flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded flex-shrink-0 ${
              !scanLoop.running
                ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
                : scanStale
                ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
            }`}
          >
            <Activity className="w-2.5 h-2.5" />
            {scanLoop.running ? (scanStale ? 'stale' : 'running') : 'stopped'}
          </span>
        )}

        {/* regime */}
        {health.intraday_regime && (
          <span className="hidden md:block text-[10px] text-muted-foreground flex-shrink-0">
            {health.intraday_regime.regime.replace(/_/g, ' ')}
          </span>
        )}

        {/* refresh spinner / last-updated */}
        <button
          onClick={e => { e.stopPropagation(); fetchHealth(); }}
          className="ml-1 p-0.5 rounded hover:bg-black/5 transition-colors flex-shrink-0"
          title="Refresh now"
        >
          <RefreshCw className={`w-3 h-3 text-muted-foreground ${loading ? 'animate-spin' : ''}`} />
        </button>

        {/* expand chevron */}
        {expanded
          ? <ChevronUp className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          : <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
      </div>

      {/* ── expanded details ──────────────────────────────────────────── */}
      {expanded && (
        <div className="mt-3 pt-3 border-t border-current/10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-xs">

          {/* Issue list */}
          {health.issues.length > 0 && (
            <div className="sm:col-span-2 lg:col-span-3">
              <p className="font-semibold text-muted-foreground mb-1">Active Issues</p>
              <ul className="space-y-1">
                {health.issues.map((issue, i) => (
                  <li key={i} className={`flex items-start gap-1.5 ${meta.text}`}>
                    <span className="mt-0.5 flex-shrink-0">•</span>
                    <span>{issue}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Deploy tier breakdown */}
          {tiers && (
            <div>
              <p className="font-semibold text-muted-foreground mb-1 flex items-center gap-1">
                <Bot className="w-3 h-3" /> Bot Tiers
              </p>
              <div className="flex flex-wrap gap-1">
                {tierBadge(tiers.live,       'live',       'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300')}
                {tierBadge(tiers.scout,      'scout',      'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300')}
                {tierBadge(tiers.shadow,     'shadow',     'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300')}
                {tierBadge(tiers.quarantine, 'quarantine', 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300')}
                {tierBadge(tiers.other,      'other',      'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300')}
              </div>
            </div>
          )}

          {/* War Room */}
          {health.war_room && (
            <div>
              <p className="font-semibold text-muted-foreground mb-1">War Room</p>
              <p className={health.war_room.stale ? 'text-amber-700 dark:text-amber-400 font-medium' : 'text-muted-foreground'}>
                {health.war_room.last_run_date
                  ? `Last run: ${health.war_room.last_run_date}${health.war_room.hours_since_run ? ` (${health.war_room.hours_since_run}h ago)` : ''}`
                  : 'Never run'}
                {health.war_room.stale && ' — stale'}
              </p>
            </div>
          )}

          {/* Circuit breaker */}
          {health.circuit_breaker && (
            <div>
              <p className="font-semibold text-muted-foreground mb-1">Circuit Breaker</p>
              <p className={
                !health.circuit_breaker.trading_allowed
                  ? 'text-red-700 dark:text-red-400 font-medium'
                  : 'text-muted-foreground'
              }>
                {health.circuit_breaker.status}
                {health.circuit_breaker.trip_reason && ` — ${health.circuit_breaker.trip_reason}`}
              </p>
            </div>
          )}

          {/* Scan loop */}
          {scanLoop && (
            <div>
              <p className="font-semibold text-muted-foreground mb-1">Scan Loop</p>
              <p className={
                !scanLoop.running ? 'text-red-700 dark:text-red-400 font-medium'
                : scanStale ? 'text-amber-700 dark:text-amber-400 font-medium'
                : 'text-muted-foreground'
              }>
                {scanLoop.running ? 'Running' : 'Stopped'}
                {scanLoop.last_tick_seconds_ago != null && ` — last tick ${scanLoop.last_tick_seconds_ago}s ago`}
                {scanStale && ' (stale)'}
              </p>
            </div>
          )}

          {/* Regime */}
          {health.intraday_regime && (
            <div>
              <p className="font-semibold text-muted-foreground mb-1">Intraday Regime</p>
              <p className={health.intraday_regime.trading_ok ? 'text-muted-foreground' : 'text-amber-700 dark:text-amber-400 font-medium'}>
                {health.intraday_regime.regime.replace(/_/g, ' ')}
                {!health.intraday_regime.trading_ok && ' — trading paused'}
              </p>
            </div>
          )}

          {/* Last refreshed */}
          {lastFetch && (
            <div className="sm:col-span-2 lg:col-span-3 text-muted-foreground/60 text-[10px]">
              Last refreshed: {lastFetch.toLocaleTimeString()}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TradingStatusBar;
